import { NgFor, NgIf } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { UserLevel } from '@reenstudio/app/core/enum/user-level';
import { Cer } from '@reenstudio/app/core/models/cer/cer';
import { Site } from '@reenstudio/app/core/models/site/site';
import { UserModel } from '@reenstudio/app/core/models/user/user-model';
import { UserApiService } from '@reenstudio/app/core/services/api/user-service';
import { CerService } from '@reenstudio/app/core/services/cer-service';
import { SiteService } from '@reenstudio/app/core/services/site-service';
import { UserService } from '@reenstudio/app/core/services/user-service';
import { MaterialModule } from '@reenstudio/app/material.module';
import { AbstractListComponent } from '@reenstudio/app/shared/components/abstract-list/abstract-list.component';
import { NoDataComponent } from '@reenstudio/app/shared/components/no-data/no-data.component';
import { StatusLabelComponent } from '@reenstudio/app/shared/components/status-label/status-label.component';
import { AlertDialogService } from '@reenstudio/app/shared/services/alert-dialog/alert-dialog.service';
import { SnackbarService } from '@reenstudio/app/shared/services/snackbar/snackbar.service';
import { TablerIconsModule } from 'angular-tabler-icons';
import { NGXLogger } from 'ngx-logger';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { first, map, merge, switchMap } from 'rxjs';

@Component({
  selector: 'app-users-list',
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.scss'],
  standalone: true,
  imports: [
    MaterialModule,
    TablerIconsModule,
    NgIf,
    NgFor,
    NgxSkeletonLoaderModule,
    NoDataComponent,
    MaterialModule,
    MatSortModule,
    MatPaginatorModule,
    RouterModule,
    StatusLabelComponent,
  ],
})
export class UsersListComponent extends AbstractListComponent {
  @Input() override displayedColumns = [
    'name',
    'email',
    'level',
    'reference',
    'state',
    'actions',
  ];

  override dataTable: MatTableDataSource<UserModel>;

  pageSize: number;
  pageIndex: number;
  pageTotal: number;

  statuses: any = {
    active: {
      text: 'Abilitato',
      color: 'success',
    },
    unactive: {
      text: 'Disattivo',
      color: 'error',
    },
  };
  sitesDetails: { siteId: number; siteName: string }[];
  cersDetails: { cerId: number; cerName: string }[];
  userLevel = UserLevel;

  constructor(
    private userApiService: UserApiService,
    private userService: UserService,
    private logger: NGXLogger,
    private snackbarService: SnackbarService,
    private alertDialogService: AlertDialogService,
    private siteService: SiteService,
    private cerService: CerService
  ) {
    super();
    this.dataTable = new MatTableDataSource<UserModel>([]);
  }

  protected override getList() {
    merge(this.filterChanged, this.sort.sortChange)
      .pipe(
        switchMap(() => {
          this.loading.next(true);
          const params = {
            userId: this.filters?.cerId,
            userName: this.filters?.cerName,
            enabled: true,
            page: this.pageIndex + 1,
            pageSize: this.pageSize,
          };

          return this.userApiService.getUsers(params);
        }),
        map((res) => {
          this.logger.debug('Users list received API data', res);

          res.data.forEach((item) => {
            this.sitesDetails = [];
            this.cersDetails = [];

            if (item.level_id === UserLevel.SITE) {
              item.sites.forEach((record) => {
                const site: Site | null =
                  this.siteService.getStorageSite(record);
                if (site) {
                  this.sitesDetails.push({
                    siteId: site.id,
                    siteName: site.description,
                  });
                }
              });
            }
            if (item.level_id === UserLevel.CER) {
              item.cers.forEach((record) => {
                const cer: Cer | null = this.cerService.getStorageCer(record);
                if (cer) {
                  this.cersDetails.push({
                    cerId: cer.id,
                    cerName: cer.description,
                  });
                }
              });
            }

            item.sitesDetails = this.sitesDetails;
            item.cersDetails = this.cersDetails;
          });
          this.dataLength = res.pagination.total;
          return res.data;
        })
      )
      .subscribe({
        next: (data) => {
          this.logger.debug('Users list dataTable data', data);
          this.dataTable.data = data;
          this.loading.next(false);
        },
      });
  }

  applyFilter(event: Event) {
    if (event) {
    }
  }

  handlePageEvent(e: PageEvent) {
    this.pageSize = e.pageSize;
    this.pageIndex = e.pageIndex;
    this.triggerFilterChange();
  }

  delete(user: number) {
    this.alertDialogService.create(
      {
        title: 'Eliminazione UTENTE con ID ' + user,
        message: 'Sei sicuro/a di voler eliminare questo utente?',
        confirm: 'Si',
        cancel: 'No',
        type: 'warning',
      },
      () => {
        this.loading.next(true);
        this.userApiService
          .deleteUser(<number>user)
          .pipe(first())
          .subscribe({
            next: () => {
              this.snackbarService.success('Utente eliminato con successo!');
            },
            error: (err) => {
              this.loading.next(false);
              let errorKeys = Object.keys(err.error.details.errors);
              let error;
              for (let key of errorKeys) {
                let currentArray = err.error.details.errors[key];
                error = currentArray[0];
              }
              this.snackbarService.error(error);
            },
            complete: () => this.triggerFilterChange(),
          });
      }
    );
  }

  getRoleNameById(id: number): string {
    const role = this.userService.getRoleById(id);
    return role ? role : 'Ruolo non trovato';
  }

  getSiteName(id: number) {
    return this.siteService.getStorageSite(id)?.description;
  }
}
